import classNames from 'classnames';
import { Fragment, useCallback, useMemo } from 'react';
import { Heading, useIsMobile } from 'yoso';
import { DonBalance } from '~/components/Balance';
import { useBalance } from '~/components/Balance/Balance.hooks';
import { breakpoints } from '~/components/Breakpoint/Breakpoint.hook';
import { currencySymbol } from '~/components/Currency/Currency.hooks';
import { myCitizenItemsUrl } from '~/components/Dosi/Dosi.hooks';
import { useEmbeddedViewer } from '~/components/Embedded/Embedded.hooks';
import { T } from '~/components/I18n';
import { Info } from '~/components/Icon';
import { DescriptionList } from '~/components/List/Description/DescriptionList';
import { useNftCount, usePurchased } from '~/components/Membership/Membership.hooks';
import { Link } from '~/components/Route/Link';
import { StatusBarAccordion } from '~/components/Status/Bar/Accordion/StatusBarAccordion';
import { StatusBarProfile } from '~/components/Status/Bar/Profile/StatusBarProfile';
import statusBarStyles from '~/components/Status/Bar/StatusBar.module.scss';
import myItemsStyles from '~/components/Status/Bar/StatusBarMyItems.module.scss';
import { Tooltip } from '~/components/Tooltip/Tooltip';
import { abbreviate, format } from '~/components/Util/number';
import styles from './LevelUpStatusBar.module.scss';

export type LevelUpStatusBarProps = {
  className?: string;
};

export const LevelUpStatusBar = ({ className }: LevelUpStatusBarProps) => {
  const isMobile = useIsMobile(breakpoints);

  const {
    data: { citizen },
  } = useNftCount({ category: 'CITIZEN' });
  const { countPerLevel = {} } = citizen || {};
  const citizenCount = useMemo(
    () =>
      Object.keys(countPerLevel).map((level) => ({
        level: Number(level.replace('lv', '')),
        count: countPerLevel[level],
      })),
    [countPerLevel]
  );

  const {
    data: { amount: donAmount },
  } = useBalance();
  const {
    data: {
      purchase: { amount: purchaseAmount },
    },
    isInitialLoading: isInitialLoadingPurchase,
  } = usePurchased();

  const { open } = useEmbeddedViewer();
  const onCitizenClick = useCallback(() => {
    open(myCitizenItemsUrl);
  }, [open]);

  return (
    <div className={classNames(styles.root, statusBarStyles.root, className)}>
      <StatusBarProfile className={classNames(styles.header, statusBarStyles.header)}>
        <div className={styles.description}>
          <T name="citizen_levelup_purchased" />{' '}
          <strong>
            {currencySymbol['USD']}
            {format(purchaseAmount, { zeroAsEmpty: isInitialLoadingPurchase })}
          </strong>
          <Tooltip
            variant="secondary"
            className={styles.tooltip}
            direction="top"
            tooltipText={<T name="citizen_levelup_info_btn" />}>
            <Info className={styles.info} />
          </Tooltip>
        </div>
      </StatusBarProfile>
      <div className={statusBarStyles.body}>
        <StatusBarAccordion>
          <Heading level={5} className={myItemsStyles.title}>
            <T name="citizen_levelup_mystatus_title" />
          </Heading>
          <DescriptionList layout="responsive" className={myItemsStyles.list}>
            <dt onClick={onCitizenClick}>Citizen</dt>
            <dd onClick={onCitizenClick}>
              <DescriptionList layout="inline" divided className={myItemsStyles.citizenList}>
                {citizenCount.map(({ level, count }) => (
                  <Fragment key={level}>
                    <dt>Lv{level}</dt>
                    <dd>{format(count, { zeroAsEmpty: true })}</dd>
                  </Fragment>
                ))}
              </DescriptionList>
            </dd>
            <dt>
              <Link to="/membership/don/history">DON</Link>
            </dt>
            <dd>
              <Link to="/membership/don/history">
                {donAmount === 0 ? '-' : <DonBalance format={isMobile ? undefined : abbreviate} />}
              </Link>
            </dd>
          </DescriptionList>
        </StatusBarAccordion>
      </div>
    </div>
  );
};
